import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import {
  DeleteQuestionnaireResponseAttachmentDocument,
  type DeleteQuestionnaireResponseAttachmentMutation,
  type DeleteQuestionnaireResponseAttachmentMutationVariables,
  type DisclosureCaseByIdQuery,
} from 'src/graphql/generated';
import { fetcher } from 'src/utils/fetch';
import type { AnswersState } from './manualIntakeDynamicQuestions.types';
import {
  cloneAnswersState,
  patchAnswersRemovingAttachment,
  patchDisclosureCaseRemovingAttachment,
} from './qaAttachmentDeleteOptimistic';

type DeleteQaAttachmentMutationVariables =
  DeleteQuestionnaireResponseAttachmentMutationVariables & {
    questionId: string;
  };

type DeleteQaAttachmentRollbackContext = {
  previousAnswers: AnswersState;
  previousDisclosure: DisclosureCaseByIdQuery | undefined;
  caseId: string | null;
};

type DeleteAttachmentOptsRef = MutableRefObject<
  { onError?: (error: unknown) => void } | undefined
>;

export function useManualIntakeDeleteQaAttachmentMutation(params: {
  answersRef: MutableRefObject<AnswersState>;
  disclosureCaseInvalidateCaseIdRef: MutableRefObject<
    string | null | undefined
  >;
  deleteAttachmentOptsRef: DeleteAttachmentOptsRef;
  setAnswers: Dispatch<SetStateAction<AnswersState>>;
}) {
  const {
    answersRef,
    disclosureCaseInvalidateCaseIdRef,
    deleteAttachmentOptsRef,
    setAnswers,
  } = params;
  const queryClient = useQueryClient();

  return useMutation<
    DeleteQuestionnaireResponseAttachmentMutation,
    unknown,
    DeleteQaAttachmentMutationVariables,
    DeleteQaAttachmentRollbackContext
  >({
    mutationFn: async (variables) => {
      const { questionId: _omitQuestionId, ...apiVariables } = variables;
      return fetcher<
        DeleteQuestionnaireResponseAttachmentMutation,
        DeleteQuestionnaireResponseAttachmentMutationVariables
      >(DeleteQuestionnaireResponseAttachmentDocument, apiVariables)();
    },
    onMutate: async (variables) => {
      const { attachmentId, questionId } = variables;
      const caseIdRaw = disclosureCaseInvalidateCaseIdRef.current?.trim();
      const caseId = caseIdRaw !== '' ? (caseIdRaw ?? null) : null;

      let previousDisclosure: DisclosureCaseByIdQuery | undefined;
      if (caseId) {
        await queryClient.cancelQueries(['DisclosureCaseById', { caseId }]);
        previousDisclosure = queryClient.getQueryData([
          'DisclosureCaseById',
          { caseId },
        ]);
        if (previousDisclosure) {
          queryClient.setQueryData(
            ['DisclosureCaseById', { caseId }],
            patchDisclosureCaseRemovingAttachment(
              JSON.parse(
                JSON.stringify(previousDisclosure),
              ) as DisclosureCaseByIdQuery,
              questionId,
              attachmentId,
            ),
          );
        }
      }

      const previousAnswers = cloneAnswersState(answersRef.current);
      setAnswers((prev) => patchAnswersRemovingAttachment(prev, attachmentId));

      return { previousAnswers, previousDisclosure, caseId };
    },
    onError: (error, variables, context) => {
      if (context?.previousAnswers != null) {
        setAnswers(context.previousAnswers);
      }
      if (context?.caseId != null && context.previousDisclosure !== undefined) {
        queryClient.setQueryData(
          ['DisclosureCaseById', { caseId: context.caseId }],
          context.previousDisclosure,
        );
      }
      const errMeta =
        error instanceof Error
          ? { message: error.message, stack: error.stack }
          : {};
      console.error({
        error,
        responseId: variables.responseId,
        attachmentId: variables.attachmentId,
        questionId: variables.questionId,
        reportId: variables.reportId,
        traceId: undefined,
        requestId: undefined,
        ...errMeta,
      });
      const opts = deleteAttachmentOptsRef.current;
      deleteAttachmentOptsRef.current = undefined;
      if (opts?.onError) {
        opts.onError(error);
      } else {
        handleErrorToast({
          toastMessage: 'Failed to remove file from questionnaire service',
        });
      }
    },
    onSuccess: () => {
      deleteAttachmentOptsRef.current = undefined;
    },
    onSettled: () => {
      const cid = disclosureCaseInvalidateCaseIdRef.current?.trim();
      if (cid) {
        void queryClient.invalidateQueries({
          queryKey: ['DisclosureCaseById', { caseId: cid }],
        });
      }
    },
  });
}
